<template>
  <header class="page-header" :class="{ 'page-header--large': large, 'page-header--with-back': back }">
    <div class="page-header__row">
      <button v-if="back" type="button" class="page-header__back" :aria-label="backLabel" @click="goBack">
        <AppIcon name="chevron-left" :size="26" />
      </button>
      <h1 class="page-header__title">
        <slot name="title">{{ title }}</slot>
      </h1>
      <div v-if="$slots.right" class="page-header__right">
        <slot name="right" />
      </div>
      <span v-else-if="back && !large" class="page-header__balance" aria-hidden="true"></span>
    </div>
    <div v-if="progress !== null || progressIndeterminate" class="page-header__progress">
      <ProgressLine
        :value="progress === null ? 0 : progress"
        :indeterminate="progressIndeterminate"
        :height="4"
        width="62%"
      />
    </div>
  </header>
</template>

<script setup>
/**
 * PageHeader — заголовок экрана.
 *
 * <PageHeader title="Юридическое согласие" back />                         // экран 2: круглая кнопка «назад»
 * <PageHeader title="Объявление участников сессии" :progress="0.4" />       // экран 4: линия прогресса
 * <PageHeader title="Повтор проигрыша" progress-indeterminate />            // экран 7
 * <PageHeader title="Таблица лидеров" large>                                // экраны 1, 9: крупный заголовок слева
 *   <template #right><StoragePill :count="stars" /></template>
 * </PageHeader>
 *
 * props: title, back (кнопка «назад»), backTo (куда идти по умолчанию, '/'), backLabel,
 *        progress (0..1 | null — без линии), progressIndeterminate, large.
 * Обработчик «назад»: @back="fn" заменяет навигацию по умолчанию (router.push(backTo)).
 * Слоты: #title (свой заголовок, например с лаймовым словом), #right.
 */
import { useRouter } from "vue-router";
import AppIcon from "./AppIcon.vue";
import ProgressLine from "./ProgressLine.vue";

const props = defineProps({
  title: { type: String, default: "" },
  back: { type: Boolean, default: false },
  backTo: { type: [String, Object], default: "/" },
  backLabel: { type: String, default: "Назад" },
  progress: { type: Number, default: null },
  progressIndeterminate: { type: Boolean, default: false },
  large: { type: Boolean, default: false },
  /** @back — если передан, вызывается вместо навигации по умолчанию. */
  onBack: { type: Function, default: null },
});

const router = useRouter();

function goBack() {
  if (props.onBack) props.onBack();
  else router.push(props.backTo);
}
</script>

<style scoped>
.page-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-top: 4px;
}

.page-header__row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
}

.page-header__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  letter-spacing: -0.01em;
}

.page-header--large .page-header__title {
  font-size: var(--cb-fs-title);
  font-weight: 800;
  text-align: left;
  letter-spacing: -0.02em;
}

.page-header__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--cb-border-strong);
  background: linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  color: var(--cb-text);
  box-shadow: var(--cb-inner-highlight);
  cursor: pointer;
  transition: transform var(--cb-dur-fast) var(--cb-ease), border-color var(--cb-dur) var(--cb-ease);
}

.page-header__back:active {
  transform: scale(0.95);
  border-color: var(--cb-lime-border);
}

/* Невидимый противовес кнопке «назад», чтобы заголовок оставался по центру. */
.page-header__balance {
  flex-shrink: 0;
  width: 52px;
}

.page-header__right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.page-header__progress {
  display: flex;
  justify-content: center;
}

@media (max-width: 380px) {
  .page-header__title {
    font-size: 20px;
  }

  .page-header--large .page-header__title {
    font-size: 28px;
  }
}
</style>
